import { useStore } from '../store/useStore';
import { formatDateTime } from '../lib/format';
import { canSystemShare, systemShare } from '../lib/native/share';
import { useT } from '../i18n/useT';
import { Modal } from './Modal';

/**
 * Surfaces the result of a share action: the read-only link (copied to
 * clipboard) or an error. A single instance is mounted by EditorPage so both
 * the toolbar button and the palette command feed the same dialog.
 */
export function ShareDialog() {
  const t = useT();
  const url = useStore((s) => s.shareUrl);
  const permission = useStore((s) => s.sharePermission);
  const error = useStore((s) => s.shareError);
  const expiresAt = useStore((s) => s.shareExpiresAt);
  const clear = useStore((s) => s.clearShare);

  if (!url && !error) return null;

  const isEdit = permission === 'edit';

  const title = url ? (isEdit ? t('share.editTitle') : t('share.readTitle')) : t('share.failTitle');

  return (
    <Modal onClose={clear} title={title}>
      {url ? (
        <>
          <p className="mb-modal-note">{isEdit ? t('share.copiedEdit') : t('share.copiedRead')}</p>
          <div className="mb-modal-urlrow">
            <input
              readOnly
              value={url}
              className="mb-modal-url"
              onFocus={(e) => e.target.select()}
            />
            <button className="mb-btn" onClick={() => void navigator.clipboard?.writeText(url)}>
              {t('common.copy')}
            </button>
          </div>
          {expiresAt != null && (
            <p className="mb-modal-note">
              {t('share.expires', { date: formatDateTime(expiresAt) })}
            </p>
          )}
          <div className="mb-modal-actions">
            {canSystemShare() && (
              <button
                className="mb-btn"
                onClick={() => void systemShare({ title: t('share.sheetTitle'), url })}
              >
                {t('share.send')}
              </button>
            )}
            <a className="mb-btn" href={url} target="_blank" rel="noreferrer">
              {t('share.openLink')}
            </a>
            <button className="mb-btn mb-btn-primary" onClick={clear}>
              {t('common.done')}
            </button>
          </div>
        </>
      ) : (
        <p className="mb-modal-note">{error}</p>
      )}
    </Modal>
  );
}
